/**
 * The types and the arithmetic behind the Recurring billing panel.
 *
 * Nothing in this repo models subscriptions: not `Contact.graphql`, not
 * `Invoice.graphql`, not the shared models. This file carried a fixture of
 * invented plans so the design was reviewable, and that fixture is gone: it
 * put plan amounts, invoice numbers and a monthly total in front of operators
 * on a live surface, indistinguishable from real billing.
 */
export type SubscriptionCadence =
  | "WEEKLY"
  | "BIWEEKLY"
  | "MONTHLY"
  | "QUARTERLY"
  | "ANNUALLY";

export type SubscriptionStatus = "ACTIVE" | "CANCELED";

export type InvoiceStatus = "PAID" | "OPEN" | "FAILED";

export interface SubscriptionInvoice {
  id: string;
  /** Invoice number as the operator reads it, not the record id. */
  number: string;
  issuedAt: number;
  amountCents: number;
  status: InvoiceStatus;
}

export interface SubscriptionCatalogItem {
  description: string;
  productName: string;
  quantity: number;
  totalCents: number;
}

export interface ContactSubscription {
  id: string;
  planName: string;
  /** Minor units, so the panel never does float arithmetic on money. */
  amountCents: number;
  cadence: SubscriptionCadence;
  status: SubscriptionStatus;
  /** Epoch ms, or null when the subscription is not scheduled to charge again. */
  nextChargeAt: number | null;
  /** Epoch ms the plan began billing. */
  startedAt: number;
  /** Newest first, which is the order the drawer states it renders them in. */
  invoices: SubscriptionInvoice[];
  catalogItem: SubscriptionCatalogItem;
}

/**
 * Cadence to charges per month, for the rolled-up monthly value.
 *
 * The weekly cadences divide the YEAR: a month is not four weeks, and 4 and 2
 * would understate a weekly plan by about 8 percent.
 */
const CHARGES_PER_MONTH: Record<SubscriptionCadence, number> = {
  WEEKLY: 52 / 12,
  BIWEEKLY: 26 / 12,
  MONTHLY: 1,
  QUARTERLY: 1 / 3,
  ANNUALLY: 1 / 12,
};

/** A canceled subscription contributes nothing to the monthly value. */
export const monthlyValueCents = (
  subscriptions: ContactSubscription[],
): number =>
  subscriptions
    .filter((subscription) => subscription.status === "ACTIVE")
    .reduce(
      (total, subscription) =>
        total +
        subscription.amountCents * CHARGES_PER_MONTH[subscription.cadence],
      0,
    );
